import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Accessibility, Hand, MessageSquare, Video, Eye, CheckCircle2, ArrowRight, Home,
  Volume2, Type, Sun, BookOpen, Sparkles,
} from 'lucide-react';

/**
 * /accessibility — Audience-aware landing route.
 *
 * Query params:
 *   ?audience=deaf            (default — Deaf/HOH/sign language)
 *   ?audience=low_vision      (large text, high contrast)
 *   ?audience=blind           (screen reader, audio readback)
 *   ?audience=low_literacy    (plain-language SOAP, audio readback)
 *   ?audience=cognitive       (AAC, simplified UI)
 *
 * Plain English. No jargon. No marketing fluff. Section 1557 / ADA-aware.
 */

const AUDIENCES = {
  deaf: {
    badge: 'For Deaf / Hard-of-Hearing patients',
    headline: <>Built for <span className="text-violet-700">every patient.</span><br/>Including ones who don't speak <span className="underline decoration-violet-300 decoration-4 underline-offset-4">with their voice.</span></>,
    intro: <>If you are <strong>Deaf, Hard-of-Hearing</strong>, use a <strong>sign language</strong>, or rely on a <strong>pictogram board</strong>, P.AI treats your communication preference as a clinical signal, not an afterthought.</>,
    primaryCta: { label: 'Start an intake with Sign Language pre-selected', href: '/?ax=sign_language' },
    secondaryCta: { label: 'I use AAC / Pictogram', href: '/?ax=aac' },
  },
  low_vision: {
    badge: 'For patients with low vision',
    headline: <>Built for <span className="text-violet-700">every patient.</span><br/>Including ones who need <span className="underline decoration-violet-300 decoration-4 underline-offset-4">larger text or higher contrast.</span></>,
    intro: <>If you have <strong>low vision</strong>, age-related macular degeneration, diabetic retinopathy, glaucoma, or simply find small text difficult to read — turn on Large Text and High Contrast using the accessibility button in the bottom-right corner of every page.</>,
    primaryCta: { label: 'Start an intake (use the toolbar to enlarge text)', href: '/?ax=low_vision' },
    secondaryCta: { label: 'Read this page aloud', href: '#audio-readback' },
  },
  blind: {
    badge: 'For Blind patients & screen reader users',
    headline: <>Built for <span className="text-violet-700">every patient.</span><br/>Including ones who use a <span className="underline decoration-violet-300 decoration-4 underline-offset-4">screen reader.</span></>,
    intro: <>P.AI's intake form and clinical output use semantic HTML, descriptive ARIA labels, and live regions that announce changes. Use NVDA, JAWS, VoiceOver, or TalkBack to navigate the same way you do any well-built site. We also offer browser-native audio readback via the accessibility toolbar.</>,
    primaryCta: { label: 'Start an intake (screen reader friendly)', href: '/?ax=screen_reader' },
    secondaryCta: { label: 'Read this page aloud', href: '#audio-readback' },
  },
  low_literacy: {
    badge: 'For patients who find medical language hard',
    headline: <>Built for <span className="text-violet-700">every patient.</span><br/>Including ones who find medical words <span className="underline decoration-violet-300 decoration-4 underline-offset-4">confusing.</span></>,
    intro: <>Doctors use big words. P.AI's intake asks you to describe what's happening in <strong>your own words</strong>. We do the translation to medical language behind the scenes. You can also have your visit summary read aloud — no need to read complex text.</>,
    primaryCta: { label: 'Start an intake in plain English', href: '/?ax=plain_language' },
    secondaryCta: { label: 'Read this page aloud', href: '#audio-readback' },
  },
  cognitive: {
    badge: 'For patients with cognitive or developmental needs',
    headline: <>Built for <span className="text-violet-700">every patient.</span><br/>Including ones with <span className="underline decoration-violet-300 decoration-4 underline-offset-4">cognitive differences.</span></>,
    intro: <>If you have a developmental disability, dementia, traumatic brain injury, or simply need extra time and visual cues — choose AAC / Pictogram mode and bring a trusted caregiver. We flag the visit so the clinic knows to allow extra time and use yes/no questions.</>,
    primaryCta: { label: 'Start with AAC / Pictogram mode', href: '/?ax=aac' },
    secondaryCta: { label: 'Read this page aloud', href: '#audio-readback' },
  },
};

export default function AccessibilityPage() {
  const [params] = useSearchParams();
  const audKey = (params.get('audience') || 'deaf').toLowerCase();
  const aud = AUDIENCES[audKey] || AUDIENCES.deaf;

  return (
    <div className="min-h-screen bg-white text-slate-900">

      {/* ── Sticky lightweight header ────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="ax-back-home">
            <img
              src="https://customer-assets.emergentagent.com/job_patient-ai-intake/artifacts/o1gistai_image.png"
              alt="P.AI"
              className="w-8 h-8 object-contain"
            />
            <span className="font-extrabold tracking-tight">
              <span className="text-[#EF4444]">P.AI</span>
              <span className="text-slate-900"> Global Health</span>
            </span>
          </Link>
          <Link to="/" className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="ax-home-link">
            <Home className="w-3.5 h-3.5" /> Home
          </Link>
        </div>
      </header>

      {/* ── Audience switcher (tiny chip row) ───────────────────────── */}
      <nav aria-label="Accessibility audience" className="max-w-5xl mx-auto px-5 pt-5">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Choose what fits you</p>
        <div className="flex flex-wrap gap-2">
          <AudienceChip k="deaf" current={audKey} label="Deaf / HOH" />
          <AudienceChip k="low_vision" current={audKey} label="Low vision" />
          <AudienceChip k="blind" current={audKey} label="Blind / screen reader" />
          <AudienceChip k="low_literacy" current={audKey} label="Plain language" />
          <AudienceChip k="cognitive" current={audKey} label="Cognitive / AAC" />
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-5 pt-10 pb-12">
        <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-900 text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 mb-6">
          <Accessibility className="w-3.5 h-3.5" /> {aud.badge}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-6">{aud.headline}</h1>
        <p className="text-lg text-slate-700 max-w-3xl mb-8">{aud.intro}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to={aud.primaryCta.href} data-testid="ax-cta-primary">
            <Button size="lg" className="bg-violet-700 hover:bg-violet-800 text-white text-base font-bold rounded-xl px-6 h-12">
              {aud.primaryCta.label}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          {aud.secondaryCta.href.startsWith('#') ? (
            <a href={aud.secondaryCta.href} data-testid="ax-cta-secondary">
              <Button size="lg" variant="outline" className="border-2 border-violet-300 text-violet-800 hover:bg-violet-50 text-base font-bold rounded-xl px-6 h-12">
                <Volume2 className="w-4 h-4 mr-2" />
                {aud.secondaryCta.label}
              </Button>
            </a>
          ) : (
            <Link to={aud.secondaryCta.href} data-testid="ax-cta-secondary">
              <Button size="lg" variant="outline" className="border-2 border-violet-300 text-violet-800 hover:bg-violet-50 text-base font-bold rounded-xl px-6 h-12">
                {aud.secondaryCta.label}
              </Button>
            </Link>
          )}
        </div>
      </section>

      {/* ── Universal tools (visible in every audience variant) ─────── */}
      <section id="audio-readback" className="bg-violet-50 border-y-2 border-violet-100">
        <div className="max-w-4xl mx-auto px-5 py-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">Tools you can use right now</h2>
          <p className="text-sm text-slate-600 mb-8 max-w-2xl">
            Every page has a violet accessibility button in the <strong>bottom-right corner</strong>. Tap it to open these tools. Your settings are remembered.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Card icon={<Type className="w-6 h-6 text-violet-700" />} title="Larger text" body="Two sizes available: Large (×1.15) and Larger (×1.30). Affects every page until you turn it off." />
            <Card icon={<Sun className="w-6 h-6 text-violet-700" />} title="High contrast" body="Yellow-on-black, AAA-friendly. Designed for low vision, AMD, diabetic retinopathy, light sensitivity." />
            <Card icon={<Volume2 className="w-6 h-6 text-violet-700" />} title="Read aloud" body="Browser-native text-to-speech reads the current page. No data leaves your device. Works in Chrome, Edge, Safari, and most modern browsers." />
            <Card icon={<Sparkles className="w-6 h-6 text-violet-700" />} title="Screen-reader friendly" body="Live regions (aria-live), descriptive labels, semantic HTML. Compatible with NVDA, JAWS, VoiceOver, TalkBack." />
          </div>
        </div>
      </section>

      {/* ── What's supported (audience-specific) ────────────────────── */}
      <AudienceDetail audKey={audKey} />

      {/* ── What we are NOT doing ───────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-5 py-14">
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">What we are NOT pretending to do</h2>
        <p className="text-sm text-slate-600 mb-8 max-w-2xl">Honesty over hype.</p>
        <ul className="space-y-4 text-base">
          <NoteItem><strong>We do not read sign language via camera.</strong> That's a multi-decade research problem, not a feature.</NoteItem>
          <NoteItem><strong>We do not auto-translate to plain language using AI.</strong> Your intake captures what you said in your own words. Your visit summary uses standard clinical language because your doctor needs it that way.</NoteItem>
          <NoteItem><strong>We do not lip-read or use AI gaze tracking.</strong> Voice intake (Sarvam STT) is silently disabled when you choose Sign Language or AAC.</NoteItem>
          <NoteItem><strong>Read-aloud uses your browser, not the cloud.</strong> No data leaves your device when you tap "Read aloud."</NoteItem>
        </ul>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-4xl mx-auto px-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            P.AI Global Health LLC · Clinical AI Prototype · No PHI in this preview
          </p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/" className="text-slate-600 hover:text-slate-900 font-semibold">Home</Link>
            <a href="mailto:sadasivam.eniasivam@paiglobalhealth.com" className="text-violet-700 hover:text-violet-900 font-semibold">Partner with us</a>
          </div>
        </div>
      </footer>

    </div>
  );
}

function AudienceChip({ k, current, label }) {
  const active = k === current;
  return (
    <Link
      to={`/accessibility?audience=${k}`}
      className={`text-xs font-bold rounded-full px-3 py-1.5 border-2 transition ${active ? 'bg-violet-700 text-white border-violet-700' : 'bg-white text-violet-800 border-violet-300 hover:bg-violet-50'}`}
      data-testid={`audience-chip-${k}`}
      aria-current={active ? 'page' : undefined}
    >
      {label}
    </Link>
  );
}

function AudienceDetail({ audKey }) {
  if (audKey === 'deaf') {
    return (
      <section className="max-w-4xl mx-auto px-5 py-14">
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">For Deaf / Hard-of-Hearing — what we actually do</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          <Card icon={<Hand className="w-6 h-6 text-violet-700" />} title="Sign language as first-class option" body="Choose ASL · ISL · BSL · LSF · Auslan in intake. Encounter flagged for clinic." />
          <Card icon={<Video className="w-6 h-6 text-violet-700" />} title="Interpreter request — in-person or VRI" body="One toggle. Choose modality: in-person, VRI, phone, family (not preferred for clinical)." />
          <Card icon={<MessageSquare className="w-6 h-6 text-violet-700" />} title="AAC / pictogram users supported" body="If you use an AAC device, mark it in intake. Voice-only flows are disabled." />
          <Card icon={<CheckCircle2 className="w-6 h-6 text-violet-700" />} title="Section 1557 / ADA" body="Federal law requires the clinic to provide a qualified interpreter at no cost to you." />
        </ul>
      </section>
    );
  }
  if (audKey === 'low_vision' || audKey === 'blind') {
    return (
      <section className="max-w-4xl mx-auto px-5 py-14">
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">{audKey === 'blind' ? 'For Blind users' : 'For low vision'} — what we actually do</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          <Card icon={<Type className="w-6 h-6 text-violet-700" />} title="Text scaling without zoom blur" body="Use the toolbar bottom-right. Two sizes available. Layout adapts — no pinch-zoom artifacts." />
          <Card icon={<Sun className="w-6 h-6 text-violet-700" />} title="High contrast (yellow on black)" body="AAA-friendly palette. Designed for AMD, diabetic retinopathy, glaucoma, light sensitivity." />
          <Card icon={<Sparkles className="w-6 h-6 text-violet-700" />} title="Screen reader semantic HTML" body="Headings, landmarks, ARIA live regions on clinical output. Tested with NVDA + VoiceOver." />
          <Card icon={<Volume2 className="w-6 h-6 text-violet-700" />} title="Browser-native audio readback" body="Tap 'Read aloud' in the toolbar. Uses your device's voice. No cloud round-trip." />
        </ul>
      </section>
    );
  }
  if (audKey === 'low_literacy') {
    return (
      <section className="max-w-4xl mx-auto px-5 py-14">
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">For plain-language users — what we actually do</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          <Card icon={<BookOpen className="w-6 h-6 text-violet-700" />} title="Tell us in your own words" body="The intake form NEVER requires medical terms. Describe what hurts, when it started, what makes it worse. We translate to clinical language for your doctor." />
          <Card icon={<Volume2 className="w-6 h-6 text-violet-700" />} title="Listen instead of reading" body="Every page has a 'Read aloud' button. Including your visit summary — no need to read complex text." />
          <Card icon={<Type className="w-6 h-6 text-violet-700" />} title="Larger text helps" body="Pair plain language with Large text size from the toolbar. Easier to focus on what matters." />
          <Card icon={<CheckCircle2 className="w-6 h-6 text-violet-700" />} title="Your doctor sees your words first" body="When you describe symptoms naturally, doctors notice things they would miss in a checkbox form. Your story matters." />
        </ul>
      </section>
    );
  }
  if (audKey === 'cognitive') {
    return (
      <section className="max-w-4xl mx-auto px-5 py-14">
        <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">For cognitive / AAC users — what we actually do</h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
          <Card icon={<MessageSquare className="w-6 h-6 text-violet-700" />} title="AAC / pictogram supported" body="Mark AAC in intake. Encounter flagged for the clinic to allow extra time and use yes/no questions." />
          <Card icon={<Hand className="w-6 h-6 text-violet-700" />} title="Caregiver-assisted intake" body="Toggle the 'Caregiver / assistant helping' switch. We log the caregiver's role for the clinic." />
          <Card icon={<Volume2 className="w-6 h-6 text-violet-700" />} title="Read aloud + larger text" body="Combine the toolbar's read-aloud with larger text for an audio-first experience." />
          <Card icon={<CheckCircle2 className="w-6 h-6 text-violet-700" />} title="SLP consult flagged" body="When AAC is signaled, our system suggests Speech-Language Pathology support for the visit when available." />
        </ul>
      </section>
    );
  }
  return null;
}

function Card({ icon, title, body }) {
  return (
    <div className="rounded-2xl bg-white border-2 border-violet-200 p-5 shadow-sm">
      <div className="flex items-center gap-3 mb-2">
        {icon}
        <h3 className="text-base font-extrabold text-slate-900">{title}</h3>
      </div>
      <p className="text-sm text-slate-700 leading-relaxed">{body}</p>
    </div>
  );
}

function NoteItem({ children }) {
  return (
    <li className="flex items-start gap-3">
      <Eye className="w-5 h-5 text-slate-400 mt-1 flex-shrink-0" />
      <span className="text-slate-700">{children}</span>
    </li>
  );
}
